import { useMemo } from 'react';
import { useStore } from '@nanostores/react';
import { atom } from 'nanostores';
import type { WritableAtom } from 'nanostores';
import type { AppBundle } from '@/utils/app-bundles';
import { getAppBundleFromUrl } from '@/utils/navigation';
import { useActiveBundle } from '@/context/active-bundle';

export interface BreadcrumbItem {
  /**
   * A unique identifier for the breadcrumb item.
   * Typically this would be a unique id for a route, or a path identifier with dynamic
   * params.
   */
  id: string;
  /**
   * The name to show for the breadcrumb item.
   * This is what will be shown in the UI.
   */
  name: string;
  /**
   * The href to link to for this breadcrumb item. This should be a correct
   * URL with all its dynamic params already resolved.
   */
  href: string;
  /**
   * Optional additional class names to apply to the breadcrumb item.
   * This can be used to apply `fs-mask` classes to hide sensitive information
   * in FullStory sessions.
   */
  className?: string;
  /**
   * Whether this breadcrumb should be truncated with a max length.
   * This should be used for breadcrumbs containing names based on
   * dynamic values, like user-defined names.
   */
  isTruncated?: boolean;
}

export const breadcrumbsStores = atom<Map<'root' | AppBundle, WritableAtom<BreadcrumbItem[]>>>(new Map());

export function getBundleBreadcrumbsStore(bundle: 'root' | AppBundle) {
  const stores = breadcrumbsStores.get();

  if (stores.has(bundle)) {
    return stores.get(bundle)!;
  }

  const newStore = atom<BreadcrumbItem[]>([]);
  const newStores = new Map(stores);
  newStores.set(bundle, newStore);
  breadcrumbsStores.set(newStores);
  return newStore;
}

export function getCurrentUrlBreadcrumbsStore() {
  const app = getAppBundleFromUrl(window.location.pathname);
  return getBundleBreadcrumbsStore(app ?? 'root');
}

export function getBreadcrumbsStore(bundle?: 'root' | AppBundle) {
  if (bundle != null) {
    return getBundleBreadcrumbsStore(bundle);
  }

  return getCurrentUrlBreadcrumbsStore();
}

export function append(item: BreadcrumbItem, bundle?: 'root' | AppBundle) {
  const store = getBreadcrumbsStore(bundle);

  const prev = store.get();
  if (prev.some((existing) => existing.id === item.id)) {
    return;
  }
  store.set([...prev, item]);
}

export function rollback(id: string, bundle?: 'root' | AppBundle) {
  const store = getBreadcrumbsStore(bundle);

  store.set(store.get().filter((existing) => existing.id !== id));
}

export function replace(item: BreadcrumbItem, bundle?: 'root' | AppBundle) {
  const store = getBreadcrumbsStore(bundle);

  const prev = store.get();
  const index = prev.findIndex((existing) => existing.id === item.id);
  if (index === -1) {
    return;
  }
  const next = [...prev];
  next[index] = item;
  store.set(next);
}

export function getLastBreadcrumb(bundle?: 'root' | AppBundle) {
  const store = getBreadcrumbsStore(bundle);
  const breadcrumbs = store.get();
  return breadcrumbs.at(-1);
}

export function getTitle(bundle?: 'root' | AppBundle) {
  const store = getBreadcrumbsStore(bundle);

  const breadcrumbs = store.get();
  if (breadcrumbs.length === 0) {
    return 'Customer.io';
  }
  const names = breadcrumbs.map((crumb) => crumb.name);
  return `${[...names].reverse().join(' | ')} | Customer.io`;
}

export type WindowHydraBreadcrumbs = {
  breadcrumbsStores: typeof breadcrumbsStores;
  append: typeof append;
  rollback: typeof rollback;
  replace: typeof replace;
  getTitle: typeof getTitle;
  getLastBreadcrumb: typeof getLastBreadcrumb;
};

Object.assign(window, {
  __hydra_breadcrumbs__: {
    breadcrumbsStores,
    append,
    rollback,
    replace,
    getTitle,
    getLastBreadcrumb,
  } satisfies WindowHydraBreadcrumbs,
});

export function useBreadcrumbs() {
  // Show breadcrumbs for the currently active app.
  // Apps might get suspended/hidden when navigating between routes,
  // but they don't get destroyed, so they wouldn't call "rollback" on their
  // breadcrumbs when they get hidden.
  // This prevents showing breadcrumbs from such hidden apps.
  const activeApp = useActiveBundle();
  const breadcrumbsStore = useMemo(() => getBreadcrumbsStore(activeApp ?? undefined), [activeApp]);
  const breadcrumbs = useStore(breadcrumbsStore);
  return { breadcrumbs, append, rollback, replace, getTitle, getLastBreadcrumb };
}
